import { useEffect, useState } from "react";
import { formatE4, formatProzent, parseEuroToE4 } from "../../shared/money.js";
import type { PreisHistorie, StammdatenVerwaltung } from "../../shared/types.js";
import type { Mischungsanteil as Mischungsanteil_ } from "./Preisfindung.js";
import { api } from "../api.js";
import { useStammdaten } from "../StammdatenContext.js";
import { BenutzerVerwaltung } from "./Benutzer.js";

type Bereich = "produkte" | "verpackungen" | "zuschlaege" | "frachtzonen" | "firma" | "benutzer";

const BEREICHE: { id: Bereich; titel: string }[] = [
  { id: "produkte", titel: "Artikel und Preise" },
  { id: "verpackungen", titel: "Verpackung" },
  { id: "zuschlaege", titel: "Zuschläge" },
  { id: "frachtzonen", titel: "Frachtzonen" },
  { id: "firma", titel: "Firmenkopf" },
  { id: "benutzer", titel: "Benutzer" },
];

export function Stammdaten({ eigeneId }: { eigeneId: string }) {
  // Die Pflege schreibt über /verwaltung, der Rest der Anwendung liest
  // /stammdaten — ohne dieses Auffrischen bliebe der Preis in der
  // Kalkulation nach einer Änderung veraltet.
  const { neuLaden: stammNeuLaden } = useStammdaten();
  const [daten, setDaten] = useState<StammdatenVerwaltung | null>(null);
  const [bereich, setBereich] = useState<Bereich>("produkte");
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  function laden() {
    api
      .get<StammdatenVerwaltung>("/verwaltung")
      .then(setDaten)
      .catch((e) => setFehler(e.message));
  }
  useEffect(laden, []);

  const melden = (m: string) => {
    setErfolg(m);
    setFehler(null);
    laden();
    stammNeuLaden();
  };
  const scheitern = (m: string) => {
    setFehler(m);
    setErfolg(null);
  };

  if (fehler && !daten)
    return (
      <div className="meldung fehler" role="alert">
        {fehler}
      </div>
    );
  if (!daten) return <p className="leer">Stammdaten werden geladen …</p>;

  return (
    <>
      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}
      {erfolg && (
        <div className="meldung erfolg" role="status">
          {erfolg}
        </div>
      )}

      <nav className="navi" style={{ marginBottom: "1.25rem", borderRadius: "var(--radius)" }}>
        <div className="navi-innen" style={{ padding: 0 }}>
          {BEREICHE.map((b) => (
            <button
              key={b.id}
              aria-current={bereich === b.id ? "page" : undefined}
              onClick={() => setBereich(b.id)}
            >
              {b.titel}
            </button>
          ))}
        </div>
      </nav>

      {bereich === "produkte" && (
        <Produkte daten={daten} beiErfolg={melden} beiFehler={scheitern} />
      )}
      {bereich === "verpackungen" && (
        <Einfach
          art="verpackung"
          titel="Verpackung"
          hinweis="Der Papiersack steckt im Basispreis (0,00). Lose Siloware bekommt einen ABSCHLAG — negativer Betrag."
          zeilen={daten.verpackungen}
          beiErfolg={melden}
          beiFehler={scheitern}
        />
      )}
      {bereich === "zuschlaege" && (
        <Einfach
          art="zuschlag"
          titel="Zuschläge"
          hinweis="Beträge je 100 kg. Werden auf den Basispreis addiert."
          zeilen={daten.zuschlaege}
          beiErfolg={melden}
          beiFehler={scheitern}
        />
      )}
      {bereich === "frachtzonen" && (
        <Einfach
          art="frachtzone"
          titel="Frachtzonen"
          hinweis="Greifen nur bei Selbstabholung. Negativer Betrag = Gutschrift der im Preis enthaltenen Fracht."
          zeilen={daten.frachtzonen}
          preisfeld="abschlag_e4"
          beiErfolg={melden}
          beiFehler={scheitern}
        />
      )}
      {bereich === "firma" && (
        <Firmenkopf werte={daten.einstellungen} beiErfolg={melden} beiFehler={scheitern} />
      )}
      {bereich === "benutzer" && <BenutzerVerwaltung eigeneId={eigeneId} />}
    </>
  );
}

/* ── Artikel und Preise ──────────────────────────────────────────────────── */

const WARENGRUPPEN = [
  { id: "mahlerzeugnis", titel: "Mahlerzeugnis" },
  { id: "futtermittel", titel: "Futtermittel" },
  { id: "pfand", titel: "Pfand" },
];

function Produkte({
  daten,
  beiErfolg,
  beiFehler,
}: {
  daten: StammdatenVerwaltung;
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
}) {
  const [neuOffen, setNeuOffen] = useState(false);
  const [historieVon, setHistorieVon] = useState<string | null>(null);
  const [mischungVon, setMischungVon] = useState<string | null>(null);
  // Voreinstellung: inaktive Artikel bleiben aus dem Weg.
  const [zeigeInaktive, setZeigeInaktive] = useState(false);

  const inaktive = daten.produkte.filter((p) => !p.aktiv).length;
  /*
   * Eine Zeile mit offenem Mischungseditor bleibt stehen, auch wenn sie
   * gerade deaktiviert wurde. Sonst verschwindet sie mitsamt ungespeicherten
   * Anteilen unter der Hand — ein Klick auf „aktiv" ist kein Verwerfen.
   */
  const sichtbar = daten.produkte.filter(
    (p) => zeigeInaktive || p.aktiv || String(p.id) === mischungVon,
  );

  return (
    <>
      {neuOffen ? (
        <NeuerArtikel
          steuersaetze={daten.steuersaetze}
          beiErfolg={(m) => {
            setNeuOffen(false);
            beiErfolg(m);
          }}
          beiFehler={beiFehler}
          beiAbbruch={() => setNeuOffen(false)}
        />
      ) : (
        <div className="knopfzeile" style={{ marginBottom: "1.25rem" }}>
          <button className="knopf" onClick={() => setNeuOffen(true)}>
            + Neuer Artikel
          </button>
        </div>
      )}

      <section className="karte">
        <h2>Artikel und Preise</h2>
        <p className="hinweistext" style={{ marginTop: 0 }}>
          Basispreise verstehen sich je 100 kg netto, frachtfrei Käuferstation einschließlich
          Papiersack. Eine Preisänderung wirkt nur auf <em>künftige</em> Angebote — bereits
          gespeicherte behalten ihre Werte.
        </p>
        <p className="hinweistext" style={{ marginTop: 0 }}>
          <strong>Ausbeute und Getreidemischung</strong> gelten nur für Mahlerzeugnisse und
          gehen in die Preisfindung ein. Der <strong>Nachproduktanteil wird gerechnet</strong>,
          nicht eingegeben: 71&nbsp;% Ausbeute heißt 29&nbsp;% Nachprodukt. Die Mischung öffnet
          sich mit einem Klick auf die Zelle.
        </p>

        {/*
          Der Zaehler ist der eigentliche Punkt: Ohne ihn sieht man nicht,
          dass etwas fehlt.
        */}
        {inaktive > 0 && (
          <label className="schalterzeile">
            <input
              type="checkbox"
              checked={zeigeInaktive}
              onChange={(e) => setZeigeInaktive(e.target.checked)}
            />
            Inaktive einblenden <span className="zahl">({inaktive})</span>
          </label>
        )}

        <div className="tabelle-scroll">
          <table>
            <thead>
              <tr>
                {/*
                  Nr., Gruppe und USt stehen unter der Bezeichnung statt in
                  eigenen Spalten — gemessen 206 px, die der Tabelle vorher
                  zum Ueberlauf fehlten. Genau so macht es die
                  Preisfindungstabelle schon.
                */}
                <th>Artikel</th>
                <th className="zahl">Basispreis €/100 kg</th>
                <th className="zahl">Ausbeute</th>
                <th className="zahl">Nachpr.</th>
                <th>Getreidemischung</th>
                <th>Aktiv</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((p) => (
                <ProduktZeile
                  key={String(p.id)}
                  p={p}
                  sorten={daten.getreidesorten ?? []}
                  mischung={(daten.mischungen ?? []).filter(
                    (m) => m.product_id === String(p.id),
                  )}
                  beiErfolg={beiErfolg}
                  beiFehler={beiFehler}
                  beiHistorie={() =>
                    setHistorieVon(historieVon === String(p.id) ? null : String(p.id))
                  }
                  historieOffen={historieVon === String(p.id)}
                  beiMischung={() =>
                    setMischungVon(mischungVon === String(p.id) ? null : String(p.id))
                  }
                  mischungOffen={mischungVon === String(p.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** Formgleich mit der Preisfindung — dort ist der Typ zu Hause. */
type Sorte = { code: string; bezeichnung: string };
type Mischungsanteil = Mischungsanteil_;

function ProduktZeile({
  p,
  sorten,
  mischung,
  beiErfolg,
  beiFehler,
  beiHistorie,
  historieOffen,
  beiMischung,
  mischungOffen,
}: {
  p: Record<string, unknown>;
  sorten: Sorte[];
  mischung: Mischungsanteil[];
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
  beiHistorie: () => void;
  historieOffen: boolean;
  beiMischung: () => void;
  mischungOffen: boolean;
}) {
  /*
   * Eine 0 ist kein Preis, sondern ein fehlender: `basispreis_e4` ist NOT
   * NULL, ein Artikel ohne Listenpreis steht deshalb mit 0 da. „0,00" waere
   * eine Behauptung — leer mit Platzhalter sagt, was Sache ist.
   */
  const [preis, setPreis] = useState(
    Number(p.basispreis_e4) > 0 ? formatE4(Number(p.basispreis_e4)) : "",
  );
  const [bemerkung, setBemerkung] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [historie, setHistorie] = useState<PreisHistorie[] | null>(null);

  // Kalkulationsfelder. Nur bei Mahlerzeugnissen sinnvoll — Pfand hat keine
  // Ausbeute, und ein leeres Feld dafuer waere eine Einladung zum Ausfuellen.
  const istMehl = p.warengruppe === "mahlerzeugnis";
  const bpText = (w: unknown) => (w == null || w === "" ? "" : formatProzent(Number(w)));
  const [ausbeute, setAusbeute] = useState(bpText(p.ausbeute_bp));

  /*
   * Der Nachproduktanteil wird gerechnet, nicht eingegeben: 71 % Ausbeute
   * heisst 29 % Nachprodukt. Der Server rechnet ihn ebenfalls selbst und
   * ignoriert, was hier steht — die Anzeige ist reine Rueckmeldung.
   */
  const nachproduktAusAusbeute = (() => {
    const n = Number(ausbeute.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0 || n > 100) return null;
    return Math.round((100 - n) * 100) / 100;
  })();

  /** Mischung gepflegt, Ausbeute nicht — dann fehlt der Artikel in der Preisfindung. */
  const ausbeuteFehlt = istMehl && mischung.length > 0 && ausbeute.trim() === "";

  const preisGeaendert = parseEuroToE4(preis) !== Number(p.basispreis_e4);
  const kalkGeaendert = istMehl && ausbeute !== bpText(p.ausbeute_bp);
  const geaendert = preisGeaendert || kalkGeaendert;

  /** "78" bzw. "77,5" -> Basispunkte. Leer bleibt leer. */
  /**
   * Dieselbe Untergrenze wie serverseitig (10 %).
   *
   * Vorher liess der Client ab 0,01 % durch und der Server wies unter 10 %
   * ab — die Meldung kam dann aus dem Netz statt aus dem Feld. Die Grenze
   * faengt den Faktor-100-Vertipper: 78 statt 7800 ergaebe, weil durch die
   * Ausbeute geteilt wird, rund 28.000 €/t.
   */
  const zuBp = (t: string): number | null | "fehler" => {
    if (t.trim() === "") return null;
    const n = Number(t.replace(",", "."));
    if (!Number.isFinite(n) || n < 10 || n > 100) return "fehler";
    return Math.round(n * 100);
  };

  useEffect(() => {
    if (historieOffen && !historie)
      api
        .get<PreisHistorie[]>(`/verwaltung/produkt/${p.id}/historie`)
        .then(setHistorie)
        .catch(() => setHistorie([]));
  }, [historieOffen, historie, p.id]);

  async function speichern() {
    const e4 = parseEuroToE4(preis);
    if (Number.isNaN(e4) || e4 < 0) return beiFehler("Ungültiger Preis.");

    const koerper: Record<string, unknown> = {
      basispreis_e4: e4,
      bemerkung: bemerkung || null,
    };

    if (kalkGeaendert) {
      const a = zuBp(ausbeute);
      if (a === "fehler") return beiFehler("Ausbeute muss zwischen 10 % und 100 % liegen.");
      koerper.ausbeute_bp = a;
    }

    setLaeuft(true);
    try {
      await api.patch(`/verwaltung/produkt/${p.id}`, koerper);
      setBemerkung("");
      setHistorie(null);
      beiErfolg(
        preisGeaendert
          ? `${p.bezeichnung}: Preis auf ${formatE4(e4)} € gesetzt.`
          : `${p.bezeichnung}: Kalkulationsdaten gespeichert.`,
      );
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  async function umschalten() {
    try {
      await api.patch(`/verwaltung/produkt/${p.id}`, { aktiv: !p.aktiv });
      beiErfolg(`${p.bezeichnung} ${p.aktiv ? "deaktiviert" : "aktiviert"}.`);
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    }
  }

  return (
    <>
      <tr style={{ opacity: p.aktiv ? 1 : 0.5 }}>
        <td>
          <div style={{ fontWeight: 600 }}>{String(p.bezeichnung)}</div>
          <div style={{ color: "var(--muted)", fontSize: "0.78em" }}>
            {String(p.sku)} · {WARENGRUPPEN.find((w) => w.id === p.warengruppe)?.titel} ·{" "}
            {Number(p.ust_bp) / 100} % USt
          </div>
        </td>
        <td>
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
            <input
              inputMode="decimal"
              value={preis}
              onChange={(e) => setPreis(e.target.value)}
              style={{ width: 100 }}
              // Ohne Platzhalter stuende bei einem Artikel ohne Listenpreis
              // nur ein leeres Kaestchen — nicht zu unterscheiden von einem,
              // dessen Wert gerade geloescht wurde.
              placeholder="kein Preis"
              aria-label="Basispreis je 100 kg"
            />
            {preisGeaendert && (
              <input
                placeholder="Grund (optional)"
                value={bemerkung}
                onChange={(e) => setBemerkung(e.target.value)}
                style={{ width: 150 }}
              />
            )}
          </div>
        </td>
        <td className="zahl">
          {istMehl ? (
            <>
              <input
                className={`zahl${ausbeuteFehlt ? " fehlt" : ""}`}
                inputMode="decimal"
                placeholder="%"
                value={ausbeute}
                onChange={(e) => setAusbeute(e.target.value)}
                style={{ width: 64 }}
                aria-label={`Ausbeute ${String(p.bezeichnung)}`}
              />
              {/*
                Eine Zeile hat zwei Speicherknoepfe: die Mischung speichert
                der Knopf im Editor, die Ausbeute der am Zeilenende. Wer die
                Mischung setzt, haelt sich plausibel fuer fertig — und der
                Artikel taucht trotzdem nicht in der Preisfindung auf.
              */}
              {ausbeuteFehlt && <div className="fehlt-hinweis">fehlt noch</div>}
            </>
          ) : (
            "\u2014"
          )}
        </td>
        <td className="zahl">
          {istMehl ? (
            <span
              className="abgeleitet"
              title="Wird aus der Ausbeute gerechnet: 100 % minus Ausbeute."
            >
              {nachproduktAusAusbeute === null
                ? "\u2014"
                : String(nachproduktAusAusbeute).replace(".", ",")}
            </span>
          ) : (
            "\u2014"
          )}
        </td>
        <td>
          {istMehl ? (
            <button
              type="button"
              className={`mischzelle${mischung.length ? "" : " leer"}`}
              aria-expanded={mischungOffen}
              onClick={beiMischung}
            >
              <span>
                {mischung.length
                  ? mischung
                      .map((m) => `${formatProzent(Number(m.anteil_bp))} % ${m.bezeichnung}`)
                      .join(" · ")
                  : "nicht gepflegt"}
              </span>
              <span className="pfeil">{mischungOffen ? "\u25b2" : "\u25bc"}</span>
            </button>
          ) : (
            "\u2014"
          )}
        </td>
        <td>
          <button className="knopf schlicht" onClick={umschalten}>
            {p.aktiv ? "aktiv" : "inaktiv"}
          </button>
        </td>
        <td>
          <div className="knopfzeile">
            <button className="knopf" onClick={speichern} disabled={!geaendert || laeuft}>
              Speichern
            </button>
            <button className="knopf schlicht" onClick={beiHistorie}>
              Verlauf
            </button>
          </div>
        </td>
      </tr>

      {mischungOffen && istMehl && (
        <tr>
          <td colSpan={7} style={{ background: "var(--engelke-blau-faded)" }}>
            <Mischungseditor
              produktId={String(p.id)}
              bezeichnung={String(p.bezeichnung)}
              sorten={sorten}
              vorhanden={mischung}
              // beiErfolg laedt die Stammdaten bereits neu — kein zweiter Abruf.
              beiErfolg={beiErfolg}
              beiFehler={beiFehler}
              beiSchliessen={beiMischung}
              ausbeuteFehlt={ausbeute.trim() === ""}
            />
          </td>
        </tr>
      )}

      {historieOffen && (
        <tr>
          <td colSpan={7} style={{ background: "var(--bg-alt)" }}>
            {!historie && <span className="hinweistext">Verlauf wird geladen …</span>}
            {historie && !historie.length && (
              <span className="hinweistext">Keine Änderungen aufgezeichnet.</span>
            )}
            {historie && historie.length > 0 && (
              <table style={{ fontSize: "0.82rem" }}>
                <tbody>
                  {historie.map((h) => (
                    <tr key={h.id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {new Date(h.geaendert_am).toLocaleString("de-DE")}
                      </td>
                      <td className="zahl">
                        {h.vorher_e4 != null ? `${formatE4(h.vorher_e4)} → ` : ""}
                        <strong>{formatE4(h.basispreis_e4)} €</strong>
                      </td>
                      <td>{h.benutzer ?? "—"}</td>
                      <td style={{ color: "var(--muted)" }}>{h.bemerkung ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Getreidemischung eines Artikels.
 *
 * Die Summe muss exakt 100 % ergeben — das prüft der Server noch einmal,
 * hier sperrt es nur den Knopf. Eine Mischung, die sich nicht schliesst,
 * ergäbe einen Preis, der plausibel aussieht und zu niedrig ist.
 */
function Mischungseditor({
  produktId,
  bezeichnung,
  sorten,
  vorhanden,
  beiErfolg,
  beiFehler,
  beiSchliessen,
  ausbeuteFehlt,
}: {
  produktId: string;
  bezeichnung: string;
  sorten: Sorte[];
  vorhanden: Mischungsanteil[];
  /** Damit die Erfolgsmeldung die verbleibende Lücke benennen kann. */
  ausbeuteFehlt: boolean;
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
  beiSchliessen: () => void;
}) {
  const [zeilen, setZeilen] = useState(
    vorhanden.map((m) => ({ sorte: m.sorte, text: formatProzent(Number(m.anteil_bp)) })),
  );
  const [laeuft, setLaeuft] = useState(false);

  const alsBp = (t: string): number | null => {
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n) && n > 0 && n <= 100 ? Math.round(n * 100) : null;
  };

  const werte = zeilen.map((z) => alsBp(z.text));
  const summe = werte.reduce<number>((t, w) => t + (w ?? 0), 0);
  const vollstaendig = zeilen.length > 0 && werte.every((w) => w !== null) && summe === 10_000;
  // Die leere Mischung ist erlaubt: So nimmt man einen Artikel bewusst aus
  // der Herleitung.
  const speicherbar = vollstaendig || zeilen.length === 0;

  const nameVon = (code: string) => sorten.find((x) => x.code === code)?.bezeichnung ?? code;
  const offeneSorten = sorten.filter((x) => !zeilen.some((z) => z.sorte === x.code));

  async function speichern() {
    setLaeuft(true);
    try {
      await api.put(`/verwaltung/produkt/${produktId}/mischung`, {
        mischung: zeilen.map((z, i) => ({ sorte: z.sorte, anteil_bp: werte[i] })),
      });
      beiErfolg(
        `${bezeichnung}: Getreidemischung gespeichert.` +
          (ausbeuteFehlt
            ? " Für die Preisfindung fehlt noch die Ausbeute — Feld in der Zeile ausfüllen und speichern."
            : ""),
      );
      beiSchliessen();
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="mischeditor">
      <div className="mischeditor-kopf">
        <h4>Getreidemischung · {bezeichnung}</h4>
        <span className={`mischsumme ${speicherbar ? "gut" : "schlecht"}`}>
          {zeilen.length === 0
            ? "keine Mischung"
            : summe === 10_000
              ? "Summe 100 %"
              : `Summe ${formatProzent(summe)} % · ${
                  summe < 10_000
                    ? `${formatProzent(10_000 - summe)} % fehlen`
                    : `${formatProzent(summe - 10_000)} % zu viel`
                }`}
        </span>
      </div>

      {zeilen.map((z, i) => (
        <div className="mischzeile" key={z.sorte}>
          <span style={{ fontWeight: 600 }}>{nameVon(z.sorte)}</span>
          <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <input
              className="zahl"
              inputMode="decimal"
              value={z.text}
              style={{ width: 70 }}
              aria-label={`Anteil ${nameVon(z.sorte)}`}
              onChange={(e) =>
                setZeilen((a) => a.map((x, j) => (i === j ? { ...x, text: e.target.value } : x)))
              }
            />
            <span className="hinweistext" style={{ margin: 0 }}>
              %
            </span>
          </span>
          <button
            className="knopf schlicht"
            aria-label={`${nameVon(z.sorte)} entfernen`}
            onClick={() => setZeilen((a) => a.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </div>
      ))}

      <div className="knopfzeile" style={{ marginTop: "0.6rem" }}>
        {offeneSorten.length > 0 && (
          <select
            value=""
            aria-label="Sorte hinzufügen"
            onChange={(e) => {
              if (!e.target.value) return;
              setZeilen((a) => [...a, { sorte: e.target.value, text: "" }]);
            }}
          >
            <option value="">+ Sorte hinzufügen …</option>
            {offeneSorten.map((x) => (
              <option key={x.code} value={x.code}>
                {x.bezeichnung}
              </option>
            ))}
          </select>
        )}
        <button className="knopf" onClick={speichern} disabled={!speicherbar || laeuft}>
          Mischung speichern
        </button>
        <button className="knopf schlicht" onClick={beiSchliessen}>
          Schließen
        </button>
        {!speicherbar && (
          <span className="hinweistext" style={{ margin: 0 }}>
            Die Anteile müssen zusammen 100 % ergeben.
          </span>
        )}
      </div>
    </div>
  );
}

function NeuerArtikel({
  steuersaetze,
  beiErfolg,
  beiFehler,
  beiAbbruch,
}: {
  steuersaetze: StammdatenVerwaltung["steuersaetze"];
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
  beiAbbruch: () => void;
}) {
  const [f, setF] = useState({
    sku: "",
    bezeichnung: "",
    warengruppe: "mahlerzeugnis",
    getreide: "",
    typ: "",
    tax_rate_id: steuersaetze.find((t) => t.satz_bp === 700)?.id ?? steuersaetze[0]?.id ?? "",
    preis: "",
  });
  const [laeuft, setLaeuft] = useState(false);
  const setzen = (t: Partial<typeof f>) => setF((a) => ({ ...a, ...t }));

  async function absenden(ev: React.FormEvent) {
    ev.preventDefault();
    const e4 = parseEuroToE4(f.preis);
    if (Number.isNaN(e4) || e4 < 0) return beiFehler("Ungültiger Basispreis.");

    setLaeuft(true);
    try {
      await api.post("/verwaltung/produkt", { ...f, basispreis_e4: e4 });
      beiErfolg(`${f.bezeichnung} wurde angelegt.`);
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <section className="karte">
      <h2>Neuer Artikel</h2>
      <form onSubmit={absenden}>
        <div className="raster">
          <div className="feld">
            <label htmlFor="a-sku">Artikelnummer</label>
            <input
              id="a-sku"
              required
              autoFocus
              value={f.sku}
              onChange={(e) => setzen({ sku: e.target.value })}
            />
          </div>
          <div className="feld" style={{ gridColumn: "span 2" }}>
            <label htmlFor="a-bez">Bezeichnung</label>
            <input
              id="a-bez"
              required
              value={f.bezeichnung}
              onChange={(e) => setzen({ bezeichnung: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="a-gruppe">Warengruppe</label>
            <select
              id="a-gruppe"
              value={f.warengruppe}
              onChange={(e) => setzen({ warengruppe: e.target.value })}
            >
              {WARENGRUPPEN.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.titel}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label htmlFor="a-getreide">Getreide</label>
            <select
              id="a-getreide"
              value={f.getreide}
              onChange={(e) => setzen({ getreide: e.target.value })}
            >
              <option value="">—</option>
              <option value="weizen">Weizen</option>
              <option value="roggen">Roggen</option>
              <option value="dinkel">Dinkel</option>
            </select>
          </div>
          <div className="feld">
            <label htmlFor="a-typ">Type (DIN 10355)</label>
            <input
              id="a-typ"
              placeholder="z. B. 550"
              value={f.typ}
              onChange={(e) => setzen({ typ: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="a-steuer">Steuersatz</label>
            <select
              id="a-steuer"
              value={f.tax_rate_id}
              onChange={(e) => setzen({ tax_rate_id: e.target.value })}
            >
              {steuersaetze.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label htmlFor="a-preis">Basispreis €/100 kg (optional)</label>
            <input
              id="a-preis"
              inputMode="decimal"
              placeholder="später nachtragen"
              value={f.preis}
              onChange={(e) => setzen({ preis: e.target.value })}
            />
            {/*
              Leer lassen ist erlaubt — den Preis leitet die Preisfindung
              ohnehin her. Aber solange keiner steht, ist der Artikel nicht
              angebotsfähig: Ohne Listenpreis rechnete eine Position mit 0
              und ergäbe mit Zuschlägen einen Betrag, der wie ein Preis
              aussieht. Die Angebotsmaske weist ihn deshalb ab.
            */}
            <span className="hinweistext">
              Kann leer bleiben. Für ein Angebot wird er gebraucht — bis dahin
              weist die Angebotsmaske den Artikel ab.
            </span>
          </div>
        </div>

        <p className="hinweistext">
          Pfandartikel (Paletten) mit Regelsatz 19 % anlegen — sie sind nach BMF-Schreiben vom
          05.11.2013 eine eigenständige Lieferung, nicht Teil der begünstigten Ware.
        </p>

        <div className="knopfzeile">
          <button className="knopf" type="submit" disabled={laeuft}>
            {laeuft ? "Wird angelegt …" : "Anlegen"}
          </button>
          <button type="button" className="knopf sekundaer" onClick={beiAbbruch}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}

/* ── Verpackung, Zuschläge, Frachtzonen ──────────────────────────────────── */

function Einfach({
  art,
  titel,
  hinweis,
  zeilen,
  preisfeld = "betrag_e4",
  beiErfolg,
  beiFehler,
}: {
  art: "verpackung" | "zuschlag" | "frachtzone";
  titel: string;
  hinweis: string;
  zeilen: Record<string, unknown>[];
  preisfeld?: string;
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
}) {
  return (
    <section className="karte">
      <h2>{titel}</h2>
      <p className="hinweistext" style={{ marginTop: 0 }}>
        {hinweis}
      </p>
      <div className="tabelle-scroll">
        <table>
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th className="zahl">Betrag €/100 kg</th>
              <th>Aktiv</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <EinfachZeile
                key={String(z.id)}
                art={art}
                z={z}
                preisfeld={preisfeld}
                beiErfolg={beiErfolg}
                beiFehler={beiFehler}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EinfachZeile({
  art,
  z,
  preisfeld,
  beiErfolg,
  beiFehler,
}: {
  art: string;
  z: Record<string, unknown>;
  preisfeld: string;
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
}) {
  const [preis, setPreis] = useState(formatE4(Number(z[preisfeld])));
  const [laeuft, setLaeuft] = useState(false);
  const geaendert = parseEuroToE4(preis) !== Number(z[preisfeld]);

  async function speichern() {
    const e4 = parseEuroToE4(preis);
    if (Number.isNaN(e4)) return beiFehler("Ungültiger Betrag.");
    setLaeuft(true);
    try {
      await api.patch(`/verwaltung/${art}/${z.id}`, { preis: e4 });
      beiErfolg(`${z.bezeichnung}: ${formatE4(e4)} €.`);
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <tr style={{ opacity: z.aktiv ? 1 : 0.5 }}>
      <td style={{ fontWeight: 600 }}>{String(z.bezeichnung)}</td>
      <td>
        <input
          inputMode="decimal"
          value={preis}
          onChange={(e) => setPreis(e.target.value)}
          style={{ width: 110 }}
        />
      </td>
      <td>
        <button
          className="knopf schlicht"
          onClick={async () => {
            try {
              await api.patch(`/verwaltung/${art}/${z.id}`, { aktiv: !z.aktiv });
              beiErfolg(`${z.bezeichnung} ${z.aktiv ? "deaktiviert" : "aktiviert"}.`);
            } catch (err) {
              beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
            }
          }}
        >
          {z.aktiv ? "aktiv" : "inaktiv"}
        </button>
      </td>
      <td>
        <button className="knopf" onClick={speichern} disabled={!geaendert || laeuft}>
          Speichern
        </button>
      </td>
    </tr>
  );
}

/* ── Firmenkopf ──────────────────────────────────────────────────────────── */

const FIRMENFELDER: { key: string; label: string; pflicht?: boolean; breit?: boolean }[] = [
  { key: "firma_name", label: "Firma", breit: true },
  { key: "firma_strasse", label: "Straße" },
  { key: "firma_plz_ort", label: "PLZ und Ort" },
  { key: "firma_registergericht", label: "Registergericht" },
  { key: "firma_hra", label: "HRA-Nummer (KG)" },
  { key: "komplementaer_firma", label: "Komplementär-GmbH", pflicht: true, breit: true },
  { key: "komplementaer_sitz", label: "Sitz der Komplementärin", pflicht: true },
  { key: "komplementaer_hrb", label: "HRB-Nummer", pflicht: true },
  { key: "geschaeftsfuehrer", label: "Geschäftsführer", pflicht: true, breit: true },
  { key: "firma_telefon", label: "Telefon" },
  { key: "firma_email", label: "E-Mail" },
];

function Firmenkopf({
  werte,
  beiErfolg,
  beiFehler,
}: {
  werte: Record<string, string>;
  beiErfolg: (m: string) => void;
  beiFehler: (m: string) => void;
}) {
  const [f, setF] = useState(werte);
  const [laeuft, setLaeuft] = useState(false);
  const offen = FIRMENFELDER.filter((x) => x.pflicht && !f[x.key]?.trim());

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    try {
      await api.patch("/verwaltung/einstellungen", f);
      beiErfolg("Firmenkopf gespeichert.");
    } catch (err) {
      beiFehler(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <section className="karte">
      <h2>Firmenkopf</h2>
      <p className="hinweistext" style={{ marginTop: 0 }}>
        Erscheint im Fuß jedes Angebots-PDF. Bei einer GmbH &amp; Co. KG ist kein persönlich
        haftender Gesellschafter eine natürliche Person — deshalb verlangt § 125 HGB in
        Verbindung mit § 35a GmbHG auf jedem Geschäftsbrief zusätzlich die Angaben zur
        Komplementärin und deren Geschäftsführer. Verstöße kann das Registergericht mit
        Zwangsgeld belegen.
      </p>

      {offen.length > 0 && (
        <div className="meldung hinweis">
          Noch offen: {offen.map((o) => o.label).join(", ")}. Diese Angaben sind Pflicht.
        </div>
      )}

      <form onSubmit={speichern}>
        <div className="raster">
          {FIRMENFELDER.map((feld) => (
            <div
              className="feld"
              key={feld.key}
              style={feld.breit ? { gridColumn: "span 2" } : undefined}
            >
              <label htmlFor={`e-${feld.key}`}>
                {feld.label}
                {feld.pflicht && <span style={{ color: "var(--engelke-rot)" }}> *</span>}
              </label>
              <input
                id={`e-${feld.key}`}
                value={f[feld.key] ?? ""}
                onChange={(e) => setF({ ...f, [feld.key]: e.target.value })}
              />
            </div>
          ))}
        </div>

        <div className="feld">
          <label htmlFor="e-fuss">Fußtext im Angebot</label>
          <textarea
            id="e-fuss"
            value={f.angebot_fusstext ?? ""}
            onChange={(e) => setF({ ...f, angebot_fusstext: e.target.value })}
          />
        </div>
        <div className="feld">
          <label htmlFor="e-agb">AGB-Hinweis</label>
          <textarea
            id="e-agb"
            value={f.agb_hinweis ?? ""}
            onChange={(e) => setF({ ...f, agb_hinweis: e.target.value })}
          />
        </div>

        <button className="knopf" type="submit" disabled={laeuft}>
          {laeuft ? "Wird gespeichert …" : "Speichern"}
        </button>
      </form>
    </section>
  );
}
